import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  ArrowsFullscreen,
  ArrowClockwise,
  ChevronDown,
  ChevronLeft,
  GeoAlt,
  GripHorizontal,
  GripVertical,
  LayoutSidebar,
  Search,
  Truck,
} from 'react-bootstrap-icons';
import { Link } from 'react-router-dom';
import withAuth from '../../auth';
import api from '../../services/axios';
import { ConfigContext } from '../ConfigContext';
import { GoogleMapsProvider } from '../google_map/GoogleMapsContext';
import { useDriverLocationRealtime } from '../../hooks/useDriverLocationRealtime';
import { useManagerPickupEvents } from '../../hooks/usePickupRealtime';
import FleetLiveMap from './FleetLiveMap';
import TruckTypeIcon from './TruckTypeIcon';
import {
  clampFleetSidebarHeight,
  clampFleetSidebarWidth,
  FLEET_SIDEBAR_DEFAULT_HEIGHT,
  FLEET_SIDEBAR_MAX_HEIGHT,
  FLEET_SIDEBAR_MIN_HEIGHT,
  FLEET_SIDEBAR_DEFAULT_WIDTH,
  FLEET_SIDEBAR_MAX_WIDTH,
  FLEET_SIDEBAR_MIN_WIDTH,
  formatRelativeTime,
  driverMatchesTruckType,
  getDriverInitials,
  getDriverTruckTypes,
  getLocationStatus,
  hasActiveDelivery,
  mergeDriverLocations,
  normalizeDriverLocations,
} from './fleetTrackingUtils';
import './FleetLiveTracking.css';

const POLL_INTERVAL_MS = 60 * 1000;
const MOBILE_BREAKPOINT = 720;

const getCollection = (response) => {
  if (Array.isArray(response)) return response;
  return Array.isArray(response?.data) ? response.data : [];
};

const fetchAllDrivers = async () => {
  const drivers = [];
  let page = 1;
  let lastPage = 1;

  do {
    const response = await api.post('/manager/drivers', {
      page,
      sort_by: 'name',
      sort_order: 'asc',
      admin_approved: 1,
    });

    drivers.push(...getCollection(response));
    lastPage = Number(response?.last_page ?? response?.meta?.last_page ?? 1);
    page += 1;
  } while (page <= lastPage);

  return drivers;
};

const statusFilters = [
  { id: 'all', label: 'All' },
  { id: 'live', label: 'Live' },
  { id: 'stale', label: 'Stale' },
  { id: 'no-location', label: 'No GPS' },
];

const FleetLiveTracking = () => {
  const config = useContext(ConfigContext);
  const workspaceRef = useRef(null);
  const [drivers, setDrivers] = useState([]);
  const [driverLocations, setDriverLocations] = useState({});
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [truckTypeFilter, setTruckTypeFilter] = useState('');
  const [selectedDriverId, setSelectedDriverId] = useState(null);
  const [fitRequest, setFitRequest] = useState(1);
  const [focusRequest, setFocusRequest] = useState(0);
  const [lastSyncAt, setLastSyncAt] = useState(null);
  const [socketConnected, setSocketConnected] = useState(false);
  const [now, setNow] = useState(Date.now());
  const [sidebarVisible, setSidebarVisible] = useState(true);
  const [sidebarWidth, setSidebarWidth] = useState(
    FLEET_SIDEBAR_DEFAULT_WIDTH,
  );
  const [sidebarHeight, setSidebarHeight] = useState(
    FLEET_SIDEBAR_DEFAULT_HEIGHT,
  );
  const [isMobileLayout, setIsMobileLayout] = useState(
    () => window.innerWidth <= MOBILE_BREAKPOINT,
  );
  const [sidebarResize, setSidebarResize] = useState(null);

  const loadFleet = useCallback(async ({ background = false } = {}) => {
    if (background) setRefreshing(true);

    try {
      const [nextDrivers, locationsResponse] = await Promise.all([
        fetchAllDrivers(),
        api.get('/manager/driver-locations'),
      ]);
      const approvedDrivers = nextDrivers.filter(
        (driver) =>
          driver?.admin_approved === true ||
          Number(driver?.admin_approved) === 1,
      );

      setDrivers(approvedDrivers);
      setDriverLocations((current) =>
        mergeDriverLocations(
          current,
          normalizeDriverLocations(getCollection(locationsResponse)),
        ),
      );
      if (!background) setFitRequest((request) => request + 1);
      setLastSyncAt(Date.now());
      setError('');
    } catch (requestError) {
      console.error('Could not load fleet tracking data:', requestError);
      setError('Fleet data could not be refreshed. Live updates will keep trying.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadFleet();
    const poll = window.setInterval(
      () => loadFleet({ background: true }),
      POLL_INTERVAL_MS,
    );
    const clock = window.setInterval(() => setNow(Date.now()), 30 * 1000);

    return () => {
      window.clearInterval(poll);
      window.clearInterval(clock);
    };
  }, [loadFleet]);

  useEffect(() => {
    if (!sidebarResize) return undefined;

    const handlePointerMove = (event) => {
      if (sidebarResize.axis === 'height') {
        setSidebarHeight(
          clampFleetSidebarHeight(
            sidebarResize.startHeight + sidebarResize.startY - event.clientY,
            workspaceRef.current?.getBoundingClientRect().height,
          ),
        );
      } else {
        setSidebarWidth(
          clampFleetSidebarWidth(
            sidebarResize.startWidth + event.clientX - sidebarResize.startX,
            window.innerWidth,
          ),
        );
      }
    };
    const handlePointerUp = () => {
      setSidebarResize(null);
      setFitRequest((request) => request + 1);
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
    window.addEventListener('pointercancel', handlePointerUp);

    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
      window.removeEventListener('pointercancel', handlePointerUp);
    };
  }, [sidebarResize]);

  useEffect(() => {
    const clampSidebarToViewport = () => {
      setIsMobileLayout(window.innerWidth <= MOBILE_BREAKPOINT);
      setSidebarWidth((width) =>
        clampFleetSidebarWidth(width, window.innerWidth),
      );
      setSidebarHeight((height) =>
        clampFleetSidebarHeight(
          height,
          workspaceRef.current?.getBoundingClientRect().height,
        ),
      );
    };

    clampSidebarToViewport();
    window.addEventListener('resize', clampSidebarToViewport);
    return () => window.removeEventListener('resize', clampSidebarToViewport);
  }, []);

  const driverIds = useMemo(() => drivers.map((driver) => driver.id), [drivers]);

  useDriverLocationRealtime({
    driverIds,
    onLocation: (event) => {
      setDriverLocations((current) =>
        mergeDriverLocations(current, normalizeDriverLocations([event])),
      );
      setNow(Date.now());
    },
    onReconnect: () => loadFleet({ background: true }),
    onConnectionChange: setSocketConnected,
  });

  useManagerPickupEvents({
    onUpdated: () => loadFleet({ background: true }),
  });

  const driverRows = useMemo(
    () =>
      drivers.map((driver) => {
        const location = driverLocations[String(driver.id)];
        return {
          driver,
          location,
          status: getLocationStatus(location, now),
          truckTypes: getDriverTruckTypes(driver),
          hasActiveDelivery: hasActiveDelivery(driver),
        };
      }),
    [driverLocations, drivers, now],
  );

  const counts = useMemo(
    () =>
      driverRows.reduce(
        (result, row) => {
          result.total += 1;
          result[row.status] += 1;
          return result;
        },
        { total: 0, live: 0, stale: 0, 'no-location': 0 },
      ),
    [driverRows],
  );

  const truckTypes = useMemo(
    () =>
      Array.from(
        driverRows
          .flatMap((row) => row.truckTypes)
          .reduce((types, truckType) => {
            const key = String(truckType?.id ?? '');
            if (!key) return types;

            types.set(key, {
              ...(types.get(key) || {}),
              ...truckType,
            });
            return types;
          }, new Map())
          .values(),
      ).sort((first, second) =>
        String(first.title || first.type || '').localeCompare(
          String(second.title || second.type || ''),
        ),
      ),
    [driverRows],
  );

  const visibleRows = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();

    return driverRows
      .filter((row) => statusFilter === 'all' || row.status === statusFilter)
      .filter(
        (row) => driverMatchesTruckType(row.truckTypes, truckTypeFilter),
      )
      .filter((row) => {
        if (!normalizedQuery) return true;

        const searchable = [
          row.driver.name,
          row.driver.email,
          row.driver.phone_num,
          ...row.truckTypes.map((truckType) =>
            truckType.title || truckType.type || '',
          ),
          ...(row.driver.vehicles || []).flatMap((vehicle) => [
            vehicle.make,
            vehicle.model,
            vehicle.plate_number,
          ]),
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();

        return searchable.includes(normalizedQuery);
      })
      .sort((first, second) => {
        const statusOrder = { live: 0, stale: 1, 'no-location': 2 };
        const statusDifference =
          statusOrder[first.status] - statusOrder[second.status];
        if (statusDifference !== 0) return statusDifference;
        return String(first.driver.name || '').localeCompare(
          String(second.driver.name || ''),
        );
      });
  }, [driverRows, query, statusFilter, truckTypeFilter]);

  const visibleDrivers = useMemo(
    () => visibleRows.map((row) => row.driver),
    [visibleRows],
  );

  const selectDriver = (driverId) => {
    setSelectedDriverId((current) =>
      String(current) === String(driverId) ? null : driverId,
    );
    if (driverLocations[String(driverId)]) {
      setFocusRequest((request) => request + 1);
    }
  };

  const primaryColor = config?.theme?.primaryColor || '#1769d2';

  const setDriversSidebarVisible = (visible) => {
    setSidebarVisible(visible);
    setFitRequest((request) => request + 1);
  };

  const handleSidebarResizeStart = (event) => {
    if (event.button !== 0) return;

    event.preventDefault();
    if (window.innerWidth <= MOBILE_BREAKPOINT) {
      setSidebarResize({
        axis: 'height',
        startY: event.clientY,
        startHeight: sidebarHeight,
      });
    } else {
      setSidebarResize({
        axis: 'width',
        startX: event.clientX,
        startWidth: sidebarWidth,
      });
    }
  };

  const handleSidebarResizeKeyDown = (event) => {
    const resizeBy = event.shiftKey ? 50 : 20;
    if (isMobileLayout) {
      let requestedHeight;

      if (event.key === 'ArrowDown') requestedHeight = sidebarHeight - resizeBy;
      if (event.key === 'ArrowUp') requestedHeight = sidebarHeight + resizeBy;
      if (event.key === 'Home') requestedHeight = FLEET_SIDEBAR_MIN_HEIGHT;
      if (event.key === 'End') requestedHeight = FLEET_SIDEBAR_MAX_HEIGHT;
      if (requestedHeight === undefined) return;

      event.preventDefault();
      setSidebarHeight(
        clampFleetSidebarHeight(
          requestedHeight,
          workspaceRef.current?.getBoundingClientRect().height,
        ),
      );
      setFitRequest((request) => request + 1);
      return;
    }

    let requestedWidth;

    if (event.key === 'ArrowLeft') requestedWidth = sidebarWidth - resizeBy;
    if (event.key === 'ArrowRight') requestedWidth = sidebarWidth + resizeBy;
    if (event.key === 'Home') requestedWidth = FLEET_SIDEBAR_MIN_WIDTH;
    if (event.key === 'End') requestedWidth = FLEET_SIDEBAR_MAX_WIDTH;
    if (requestedWidth === undefined) return;

    event.preventDefault();
    setSidebarWidth(
      clampFleetSidebarWidth(requestedWidth, window.innerWidth),
    );
    setFitRequest((request) => request + 1);
  };

  return (
    <section
      className="fleet-live-page"
      style={{ '--fleet-primary': primaryColor }}
    >
      <div
        ref={workspaceRef}
        className={`fleet-live-workspace ${
          sidebarVisible ? 'sidebar-visible' : 'sidebar-hidden'
        } ${sidebarResize ? 'sidebar-resizing' : ''}`}
        style={{
          '--fleet-sidebar-width': `${sidebarWidth}px`,
          '--fleet-sidebar-height': `${sidebarHeight}px`,
        }}
      >
        <GoogleMapsProvider>
          <FleetLiveMap
            drivers={visibleDrivers}
            driverLocations={driverLocations}
            selectedDriverId={selectedDriverId}
            onSelectDriver={selectDriver}
            fitRequest={fitRequest}
            focusRequest={focusRequest}
            now={now}
            sidebarLeftPadding={sidebarVisible ? sidebarWidth + 32 : 80}
            sidebarBottomPadding={
              sidebarVisible && isMobileLayout ? sidebarHeight + 32 : 50
            }
          />
        </GoogleMapsProvider>

        {!sidebarVisible && (
          <button
            type="button"
            className="fleet-sidebar-show-button"
            onClick={() => setDriversSidebarVisible(true)}
            aria-label="Show drivers sidebar"
          >
            <LayoutSidebar aria-hidden="true" />
            <span>Show drivers</span>
          </button>
        )}

        {sidebarVisible && (
          <aside className="fleet-sidebar" aria-label="Fleet drivers">
            <div
              className="fleet-sidebar-resize-handle"
              role="separator"
              aria-label="Resize drivers sidebar"
              aria-orientation={isMobileLayout ? 'horizontal' : 'vertical'}
              aria-valuemin={
                isMobileLayout
                  ? FLEET_SIDEBAR_MIN_HEIGHT
                  : FLEET_SIDEBAR_MIN_WIDTH
              }
              aria-valuemax={
                isMobileLayout
                  ? FLEET_SIDEBAR_MAX_HEIGHT
                  : FLEET_SIDEBAR_MAX_WIDTH
              }
              aria-valuenow={Math.round(
                isMobileLayout ? sidebarHeight : sidebarWidth,
              )}
              tabIndex={0}
              onPointerDown={handleSidebarResizeStart}
              onKeyDown={handleSidebarResizeKeyDown}
              title={`Drag to resize the drivers sidebar by ${
                isMobileLayout ? 'height' : 'width'
              }`}
            >
              <GripVertical
                className="fleet-sidebar-grip-desktop"
                aria-hidden="true"
              />
              <GripHorizontal
                className="fleet-sidebar-grip-mobile"
                aria-hidden="true"
              />
            </div>
            <div className="fleet-sidebar-heading">
              <div className="fleet-sidebar-title">
                <h2>Drivers</h2>
                <span className="fleet-result-count">{visibleRows.length}</span>
              </div>
              <div className="fleet-sidebar-heading-actions">
                <span
                  className={`fleet-connection ${socketConnected ? 'connected' : ''}`}
                  title={
                    socketConnected
                      ? 'Realtime connected'
                      : 'Realtime reconnecting'
                  }
                >
                  <span />
                  <span className="fleet-connection-label">
                    {socketConnected ? 'Realtime' : 'Reconnecting'}
                  </span>
                </span>
                <button
                  type="button"
                  className="fleet-sidebar-icon-button"
                  onClick={() => setFitRequest((request) => request + 1)}
                  title="Fit all visible drivers"
                  aria-label="Fit all visible drivers"
                >
                  <ArrowsFullscreen aria-hidden="true" />
                </button>
                <button
                  type="button"
                  className="fleet-sidebar-icon-button primary"
                  onClick={() => loadFleet({ background: true })}
                  disabled={refreshing}
                  title={refreshing ? 'Refreshing fleet' : 'Refresh fleet'}
                  aria-label={refreshing ? 'Refreshing fleet' : 'Refresh fleet'}
                >
                  <ArrowClockwise
                    className={refreshing ? 'spinning' : ''}
                    aria-hidden="true"
                  />
                </button>
                <button
                  type="button"
                  className="fleet-sidebar-icon-button"
                  onClick={() => setDriversSidebarVisible(false)}
                  title="Hide drivers sidebar"
                  aria-label="Hide drivers sidebar"
                >
                  {isMobileLayout ? (
                    <ChevronDown aria-hidden="true" />
                  ) : (
                    <ChevronLeft aria-hidden="true" />
                  )}
                </button>
              </div>
            </div>

          <label className="fleet-search">
            <Search aria-hidden="true" />
            <span className="sr-only">Search drivers</span>
            <input
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search driver or vehicle"
            />
          </label>

          <div
            className="fleet-status-filters"
            role="group"
            aria-label="Location status"
          >
            {statusFilters.map((filter) => (
              <button
                key={filter.id}
                type="button"
                className={statusFilter === filter.id ? 'active' : ''}
                onClick={() => setStatusFilter(filter.id)}
              >
                {filter.label}
                <span>{filter.id === 'all' ? counts.total : counts[filter.id]}</span>
              </button>
            ))}
          </div>

          <div className="fleet-truck-filter">
            <div className="fleet-truck-filter-heading">
              <span>Vehicle type</span>
              {(statusFilter !== 'all' || truckTypeFilter) && (
                <button
                  type="button"
                  onClick={() => {
                    setStatusFilter('all');
                    setTruckTypeFilter('');
                  }}
                >
                  Clear filters
                </button>
              )}
            </div>
            <div
              className="fleet-truck-filter-options"
              role="group"
              aria-label="Filter drivers by vehicle type"
            >
              <button
                type="button"
                className={!truckTypeFilter ? 'active' : ''}
                onClick={() => setTruckTypeFilter('')}
                aria-pressed={!truckTypeFilter}
                title="Every vehicle type"
              >
                <TruckTypeIcon />
                <span>All</span>
              </button>
              {truckTypes.map((truckType) => {
                const value = String(truckType.id);
                const label = truckType.title || truckType.type || 'Vehicle';
                return (
                  <button
                    key={value}
                    type="button"
                    className={truckTypeFilter === value ? 'active' : ''}
                    onClick={() => setTruckTypeFilter(value)}
                    aria-pressed={truckTypeFilter === value}
                    title={label}
                  >
                    <TruckTypeIcon truckType={truckType} />
                    <span>{label}</span>
                  </button>
                );
              })}
            </div>
          </div>
          {error && <div className="fleet-error" role="alert">{error}</div>}
          <div className="fleet-driver-list">
            {loading ? (
              <div className="fleet-list-state">
                <span className="fleet-map-spinner" />
                <p>Loading drivers...</p>
              </div>
            ) : visibleRows.length === 0 ? (
              <div className="fleet-list-state">
                <GeoAlt />
                <h3>No drivers found</h3>
                <p>Try another search or status filter.</p>
              </div>
            ) : (
              visibleRows.map(({ driver, location, status, truckTypes, hasActiveDelivery }) => {
                const selected = String(driver.id) === String(selectedDriverId);
                const truckLabel =
                  truckTypes
                    .map((truckType) => truckType.title || truckType.type)
                    .filter(Boolean)
                    .join(', ') || 'Vehicle not assigned';
                return (
                  <article
                    key={driver.id}
                    className={`fleet-driver-card ${status} ${selected ? 'selected' : ''}`}
                  >
                    <div className="fleet-driver-select">
                      <Link
                        className="fleet-driver-avatar-link"
                        to={`/drivers/${driver.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={`Open details for ${driver.name || `driver ${driver.id}`} in a new tab`}
                      >
                        <span className="fleet-driver-avatar">
                          {getDriverInitials(driver.name)}
                          {driver.profile_photo && (
                            <img
                              src={driver.profile_photo}
                              alt=""
                              onError={(event) => {
                                event.currentTarget.hidden = true;
                              }}
                            />
                          )}
                          <span className={`fleet-location-dot ${status}`} />
                        </span>
                      </Link>
                      <span className="fleet-driver-copy">
                        <span className="fleet-driver-name-row">
                          <span className="fleet-driver-identity">
                            <Link
                              className="fleet-driver-name-link"
                              to={`/drivers/${driver.id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              #{driver.id} {driver.name || 'Driver'}
                            </Link>
                          </span>
                          <span className={`fleet-delivery-state ${hasActiveDelivery ? 'active' : ''}`}>
                            {hasActiveDelivery ? 'On delivery' : 'No active delivery'}
                          </span>
                        </span>
                        <button
                          type="button"
                          className="fleet-driver-map-focus"
                          onClick={() => selectDriver(driver.id)}
                          aria-pressed={selected}
                          aria-label={`Focus ${driver.name || `driver ${driver.id}`} on the map`}
                        >
                          <span className="fleet-driver-vehicle">
                            <Truck aria-hidden="true" />
                            {truckLabel}
                          </span>
                          <span className="fleet-driver-location">
                            <GeoAlt aria-hidden="true" />
                            <span className={`fleet-status-label ${status}`}>
                              {status === 'live'
                                ? 'Live'
                                : status === 'stale'
                                  ? 'Stale'
                                  : 'No GPS'}
                            </span>
                            <span>
                              {formatRelativeTime(location?.updatedAt, now)}
                            </span>
                          </span>
                        </button>
                      </span>
                    </div>
                  </article>
                );
              })
            )}
          </div>
          <div className="fleet-sidebar-footer">
            <span><i className="live" />Live</span>
            <span><i className="stale" />Stale</span>
            <span><i className="offline" />No GPS</span>
            {lastSyncAt && (
              <small>Synced {formatRelativeTime(lastSyncAt, now)}</small>
            )}
          </div>
          </aside>
        )}
      </div>
    </section>
  );
};

export default withAuth(FleetLiveTracking);
